import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, forkJoin } from 'rxjs';
import { distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { ISvcSelectOption } from '@solvace/component/svc-controls';
import { SvcMasterdataService } from 'projects/lib-shared-core/src/lib/services/svc-masterdata.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SvcControlsModule } from '@solvace/component/svc-controls';
import { SvcButtonsModule } from '@solvace/component/svc-buttons';
import { SvcSlideToggleModule } from '@solvace/component/svc-slide-toggle';
import { UserService } from 'projects/lib-shared-core/src/lib/features/user/services/user.service';
import { CheckinFilterService } from 'projects/lib-shared-core/src/lib/services/checkin-filter/checkin-filter.service';

export interface ModalCheckinData {
  places?: ISvcSelectOption[];
  equipments?: ISvcSelectOption[];
  selectedPlaceId?: string | number;
  selectedEquipmentId?: string | number;
  dontAskAgain?: boolean;
}

export interface ModalCheckinResult {
  placeId?: string | number | null;
  placeName?: string | null;
  equipmentId?: string | number | null;
  equipmentName?: string | null;
  dontAskAgain: boolean;
}

@Component({
  selector: 'modal-checkin',
  templateUrl: './modal-checkin.component.html',
  styleUrls: ['./modal-checkin.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatDividerModule,
    TranslocoModule,
    SvcControlsModule,
    SvcButtonsModule,
    SvcSlideToggleModule,
  ],
})
export class ModalCheckinComponent implements OnInit, OnDestroy {
  form: FormGroup;
  places: ISvcSelectOption[];
  equipments: ISvcSelectOption[];
  isLoadingPlaces = false;
  isLoadingEquipments = false;

  private readonly _destroy$ = new Subject<void>();
  private _lastEquipmentRequestFor: string | number | null = null;
  private readonly _checkinRecents: { udaId?: number | string; equipmentId?: number | string }[];

  constructor(
    private _formBuilder: FormBuilder,
    private _dialogRef: MatDialogRef<ModalCheckinComponent, ModalCheckinResult>,
    private _masterdataService: SvcMasterdataService,
    private _translocoService: TranslocoService,
    private _userService: UserService,
    private _checkinFilterService: CheckinFilterService,
    @Inject(MAT_DIALOG_DATA) public data: ModalCheckinData
  ) {
    this.places = data?.places ?? [];
    this.equipments = data?.equipments ?? [];
    this._checkinRecents = [...(this._userService.user?.checkInFilter?.recent ?? [])];
    const selectedPlaceId = data?.selectedPlaceId ?? null;
    const selectedEquipmentId = data?.selectedEquipmentId ?? null;

    if (this.places?.length) {
      const recentPlaceIds = this._checkinRecents
        .map((item) => item?.udaId)
        .filter((value) => value !== null && value !== undefined);
      this.places = this._buildOptionsWithRecents(this.places, recentPlaceIds);
    }

    this.form = this._formBuilder.group({
      placeId: [selectedPlaceId],
      equipmentId: [selectedEquipmentId ?? null],
      dontAskAgain: [data?.dontAskAgain ?? false],
    });
    if (!selectedPlaceId) {
      this.form.get('equipmentId')?.disable({ emitEvent: false });
    }

    if (this.equipments?.length && selectedPlaceId) {
      const recentEquipments = this._getRecentEquipmentIdsForPlace(selectedPlaceId);
      this.equipments = this._buildOptionsWithRecents(this.equipments, recentEquipments);
    }
  }

  ngOnInit(): void {
    // Skip initial load so we avoid showing a pending spinner during visual validation

    this._loadLocations();

    const initialPlaceId = this.form.get('placeId')?.value;
    if (initialPlaceId) {
      this._loadEquipments(initialPlaceId);
    }

    this.form
      .get('placeId')
      ?.valueChanges.pipe(takeUntil(this._destroy$), distinctUntilChanged())
      .subscribe((placeId) => {
        this.form.get('equipmentId')?.reset(null, { emitEvent: false });
        this.equipments = [];
        if (!placeId) {
          this.form.get('equipmentId')?.disable({ emitEvent: false });
          return;
        }
        this.form.get('equipmentId')?.enable({ emitEvent: false });
        if (placeId) {
          this._loadEquipments(placeId);
        }
      });

    // Only fetch equipments after the place is selected
  }

  private _loadLocations(): void {
    this.isLoadingPlaces = true;
    forkJoin({
      departments: this._masterdataService.getDepartments(true),
      areas: this._masterdataService.getAreas(true),
      locations: this._masterdataService.getLocations(true),
    })
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => (this.isLoadingPlaces = false))
      )
      .subscribe({
        next: ({ locations }) => {
          const locationOptions =
            (locations ?? []).map((location) => ({
              text: location?.udaName ?? '',
              value: location?.udaId,
            })) ?? [];
          const recentPlaceIds = this._checkinRecents
            .map((item) => item?.udaId)
            .filter((value) => value !== null && value !== undefined);
          this.places = this._buildOptionsWithRecents(locationOptions, recentPlaceIds);
        },
        error: () => {
          this.isLoadingPlaces = false;
        },
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onClose(): void {
    this._dialogRef.close();
  }

  onConfirm(): void {
    const placeId = this.form.get('placeId')?.value ?? null;
    const equipmentId = this.form.get('equipmentId')?.value ?? null;
    const result: ModalCheckinResult = {
      placeId,
      placeName: this._resolveOptionLabel(this.places, placeId),
      equipmentId,
      equipmentName: this._resolveOptionLabel(this.equipments, equipmentId),
      dontAskAgain: this.form.get('dontAskAgain')?.value ?? false,
    };

    this._dialogRef.close(result);
  }

  private _loadEquipments(placeId: string | number): void {
    this._lastEquipmentRequestFor = placeId;
    this.isLoadingEquipments = true;
    this._checkinFilterService
      .loadEquipmentsByLocation(placeId)
      .pipe(takeUntil(this._destroy$), finalize(() => (this.isLoadingEquipments = false)))
      .subscribe({
        next: (equipments) => {
          if (this._lastEquipmentRequestFor !== placeId) return;
          const equipmentOptions =
            (equipments ?? []).map((equipment) => ({
              text: equipment.equipmentName,
              value: equipment.equipmentId,
            })) ?? [];
          const recentEquipmentIds = this._getRecentEquipmentIdsForPlace(placeId);
          this.equipments = this._buildOptionsWithRecents(equipmentOptions, recentEquipmentIds);
        },
        error: () => {
          if (this._lastEquipmentRequestFor !== placeId) return;
          this.equipments = [];
        },
      });
  }

  private _getRecentEquipmentIdsForPlace(placeId: string | number): Array<string | number> {
    const placeKey = this._getValueKey(placeId);
    return this._checkinRecents
      .filter((item) => this._getValueKey(item?.udaId) === placeKey)
      .map((item) => item?.equipmentId)
      .filter((value) => value !== null && value !== undefined);
  }

  private _buildOptionsWithRecents(options: ISvcSelectOption[], recentValues: Array<string | number>): ISvcSelectOption[] {
    if (!options?.length) {
      return [];
    }

    const optionMap = new Map<string, ISvcSelectOption>();
    options.forEach((option) => {
      const key = this._getValueKey(option.value);
      if (key) {
        optionMap.set(key, option);
      }
    });

    const seen = new Set<string>();
    const recentOptions: ISvcSelectOption[] = [];
    (recentValues ?? []).forEach((value) => {
      const key = this._getValueKey(value);
      if (!key || seen.has(key)) return;
      const match = optionMap.get(key);
      if (!match) return;
      recentOptions.push({ ...match });
      seen.add(key);
    });

    const recentLabel = this._translocoService.translate('Recentes');
    const allLabel = this._translocoService.translate('Todos');
    const recentGrouped = recentOptions.map((option) => ({
      ...option,
      groupKey: 'recent',
      groupLabel: recentLabel,
    }));
    const allGrouped = options.map((option) => ({
      ...option,
      groupKey: 'all',
      groupLabel: allLabel,
    }));

    if (!recentGrouped.length) {
      return allGrouped;
    }

    return [...recentGrouped, ...allGrouped];
  }

  private _getValueKey(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    return value.toString();
  }

  private _resolveOptionLabel(options: ISvcSelectOption[], value: string | number | null): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const key = this._getValueKey(value);
    const match = options?.find((option) => this._getValueKey(option.value) === key);
    return match?.text ?? null;
  }
}
